import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Solo visualización: muestra los reportes uno junto a otro con desplazamiento horizontal.
export function PantallaCompleta({ titulo, reportes, onClose }) {
  const ref = useRef(null);
  const scroller = useRef(null);
  const panels = useRef([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const el = ref.current;
    el?.requestFullscreen?.().catch(() => {});
    const onKey = (e) => { if (e.key === "Escape" && !document.fullscreenElement) onClose(); };
    const onFs = () => { if (!document.fullscreenElement) onClose(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      document.body.style.overflow = "";
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, [onClose]);

  const ir = (i) => {
    const n = Math.max(0, Math.min(reportes.length - 1, i));
    const p = panels.current[n];
    if (p) scroller.current.scrollTo({ left: p.offsetLeft - 24, behavior: "smooth" });
    setIdx(n);
  };
  const onScroll = () => {
    const x = scroller.current.scrollLeft + 48;
    let n = 0;
    panels.current.forEach((p, i) => { if (p && p.offsetLeft <= x) n = i; });
    const fin = scroller.current.scrollLeft + scroller.current.clientWidth >= scroller.current.scrollWidth - 4;
    setIdx(fin ? reportes.length - 1 : n);
  };

  return (
    <div ref={ref} className="fixed inset-0 z-[60] flex flex-col bg-slate-100" data-testid="pc-overlay">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-6 py-3 shadow-sm">
        <div className="mr-auto">
          <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Pantalla completa</p>
          <p className="font-display text-lg font-extrabold text-slate-900" data-testid="pc-titulo">{titulo}</p>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="pc-indicadores">
          {reportes.map((r, i) => (
            <button key={r.key} type="button" onClick={() => ir(i)} data-testid={`pc-ir-${r.key}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${i === idx ? "bg-[var(--gf-title)] text-white" : "text-slate-600 hover:bg-white"}`}>{r.label}</button>
          ))}
        </div>
        <Button variant="outline" size="icon" onClick={() => ir(idx - 1)} disabled={idx === 0} aria-label="Reporte anterior" data-testid="pc-anterior"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" onClick={() => ir(idx + 1)} disabled={idx === reportes.length - 1} aria-label="Reporte siguiente" data-testid="pc-siguiente"><ChevronRight className="h-4 w-4" /></Button>
        <Button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pc-salir"><Minimize2 className="mr-2 h-4 w-4" />Salir</Button>
      </div>
      <div ref={scroller} onScroll={onScroll} className="flex flex-1 items-start gap-8 overflow-auto scroll-smooth px-6 py-6" data-testid="pc-scroller">
        {reportes.map((r, i) => (
          <section key={r.key} ref={(el) => { panels.current[i] = el; }} className="w-max shrink-0 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm" data-testid={`pc-panel-${r.key}`}>
            <p className="mb-3 rounded-lg bg-[var(--gf-title)] px-4 py-1.5 text-left text-base font-bold text-white">{r.label}</p>
            {r.node}
          </section>
        ))}
        <div className="w-1 shrink-0" aria-hidden />
      </div>
    </div>
  );
}
